import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Stack, CircularProgress,
  Alert, Chip, Modal, ModalDialog, ModalClose, Divider, Snackbar
} from '@mui/joy';
import {
  Sparkle as AutoAwesome, Tag, CheckCircle, ClockClockwise as PendingActions, Archive, Globe
} from '@phosphor-icons/react';
import { useTopicalMaps } from '../../hooks/useTopicalMaps';
import { useBlogIdeas } from '../../hooks/useBlogIdeas';
import { BlogIdeaList } from './BlogIdeaList';
import { ImportWebsitePagesModal } from './ImportWebsitePagesModal';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import type { ImportFromWebsiteResponse } from '../../types/marketing';

interface TopPage {
  id: number;
  url: string;
  keyword_1: string;
  keyword_2?: string;
  keyword_3?: string;
  company: number;
}

export const TopicalMapManager: React.FC = () => {
  const [selectedTopPage, setSelectedTopPage] = useState<TopPage | null>(null);
  const [showBlogIdeas, setShowBlogIdeas] = useState(false);
  const [selectedMapId, setSelectedMapId] = useState<number | null>(null);
  const [generatingPages, setGeneratingPages] = useState<Set<number>>(new Set());
  const [showImportModal, setShowImportModal] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; color: 'success' | 'warning' }>({
    open: false, message: '', color: 'success',
  });
  // When a top page was confirmed to have a map server-side (import response /
  // generate call returned): lets us tell "generation failed, map deleted"
  // apart from "map not created yet" when fresh poll data has no map.
  const seededAtRef = useRef<Record<number, number>>({});

  const { generateTopicalMap, approveTopicalMap, isGenerating } = useTopicalMaps();
  const { blogIdeas, isLoading: ideasLoading, refetch: refetchIdeas } = useBlogIdeas(selectedMapId);
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch top pages
  const { data: topPages, isLoading: pagesLoading } = useQuery({
    queryKey: ['top-pages'],
    queryFn: async () => {
      const response = await api.get('/api/top-pages/');
      return (response.data as any).results || response.data;
    }
  });

  // Fetch topical maps
  const { data: topicalMaps, refetch: refetchMaps, dataUpdatedAt } = useQuery({
    queryKey: ['topical-maps'],
    queryFn: async () => {
      const response = await api.get('/api/marketing/topical-maps/');
      return (response.data as any).results || response.data;
    },
    refetchInterval: generatingPages.size > 0 ? 2000 : false, // Poll every 2 seconds when generating
  });

  const handleGenerateMap = async (topPage: TopPage) => {
    try {
      // Add to generating set
      setGeneratingPages(prev => new Set(prev).add(topPage.id));

      await generateTopicalMap(topPage.id);
      // The map now exists server-side; from here on, "no map in fresh data"
      // means the generation task failed and deleted it.
      seededAtRef.current[topPage.id] = Date.now();

      // The polling will handle checking for completion
      // We'll remove from generating set when we detect the map has ideas
    } catch (error) {
      console.error('Failed to generate topical map:', error);
      // Remove from generating set on error
      setGeneratingPages(prev => {
        const newSet = new Set(prev);
        newSet.delete(topPage.id);
        return newSet;
      });
    }
  };

  const handleImported = (result: ImportFromWebsiteResponse) => {
    if (result.queued.length > 0) {
      const now = Date.now();
      setGeneratingPages(prev => {
        const next = new Set(prev);
        result.queued.forEach(q => {
          next.add(q.top_page_id);
          seededAtRef.current[q.top_page_id] = now;
        });
        return next;
      });
    }
    queryClient.invalidateQueries({ queryKey: ['top-pages'] });
    queryClient.invalidateQueries({ queryKey: ['topical-maps'] });
    queryClient.invalidateQueries({ queryKey: ['content-stats'] });

    const skippedNote = result.skipped.length > 0
      ? ` ${result.skipped.length} page${result.skipped.length === 1 ? ' was' : 's were'} skipped (already imported).`
      : '';
    if (result.queued.length === 0) {
      setSnackbar({ open: true, color: 'warning', message: 'All selected pages were already imported.' });
    } else {
      setSnackbar({
        open: true,
        color: 'success',
        message: `Importing ${result.queued.length} page${result.queued.length === 1 ? '' : 's'} — choosing keywords and generating topical maps...${skippedNote}`,
      });
    }
  };

  // Check for completed generations
  useEffect(() => {
    if (topicalMaps && generatingPages.size > 0) {
      const updatedGenerating = new Set(generatingPages);

      generatingPages.forEach(pageId => {
        const map = topicalMaps.find((m: any) => m.top_page === pageId);
        // If map exists and has ideas, generation is complete
        if (map && map.ideas_count > 0) {
          updatedGenerating.delete(pageId);
          return;
        }
        // If we know the map existed (seeded) and data fresher than that
        // moment has no active map, generation failed and deleted it — stop
        // the spinner instead of polling forever.
        const seededAt = seededAtRef.current[pageId] ?? Infinity;
        const activeMap = topicalMaps.find(
          (m: any) => m.top_page === pageId && (m.status === 'draft' || m.status === 'approved')
        );
        if (!activeMap && dataUpdatedAt > seededAt) {
          updatedGenerating.delete(pageId);
        }
      });

      if (updatedGenerating.size !== generatingPages.size) {
        setGeneratingPages(updatedGenerating);
      }
    }
  }, [topicalMaps, generatingPages, dataUpdatedAt]);

  const handleViewIdeas = (topPage: TopPage, mapId: number) => {
    setSelectedTopPage(topPage);
    setSelectedMapId(mapId);
    setShowBlogIdeas(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'draft': return 'warning';
      case 'archived': return 'neutral';
      default: return 'neutral';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return <CheckCircle />;
      case 'draft': return <PendingActions />;
      case 'archived': return <Archive />;
      default: return <PendingActions />;
    }
  };

  if (pagesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const canImport = !!user?.is_organization_admin;
  const importButton = canImport ? (
    <Button
      variant="outlined"
      color="primary"
      startDecorator={<Globe />}
      onClick={() => setShowImportModal(true)}
    >
      Import From Website
    </Button>
  ) : null;

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'flex-start' },
          gap: 1.5,
        }}
      >
        <Box>
          <Typography level="h3" sx={{ mb: 1 }}>Topical Maps</Typography>
          <Typography level="body-md" sx={{ color: 'text.secondary' }}>
            Generate SEO-optimized blog ideas from your top pages' focus keywords
          </Typography>
        </Box>
        {importButton}
      </Box>

      {topPages?.length === 0 ? (
        <Alert color="neutral">
          <Stack spacing={1.5} alignItems="flex-start">
            <Typography>
              No top pages found. Create top pages with focus keywords in the Local SEO section
              {canImport ? ', or import the pages of your generated website below.' : ' first.'}
            </Typography>
            {importButton}
          </Stack>
        </Alert>
      ) : (
        <Stack spacing={2}>
          {topPages?.map((page: TopPage) => {
            const map = topicalMaps?.find((m: any) => m.top_page === page.id);
            return (
              <Card key={page.id} variant="outlined">
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', md: 'row' },
                      justifyContent: 'space-between',
                      alignItems: { xs: 'stretch', md: 'flex-start' },
                      gap: 1.5,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        level="h4"
                        sx={{
                          mb: 1,
                          wordBreak: 'break-word',
                          fontSize: { xs: '1rem', md: '1.25rem' },
                        }}
                      >
                        {page.url || 'Top Page'}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                        {[page.keyword_1, page.keyword_2, page.keyword_3]
                          .filter(Boolean)
                          .map((keyword, idx) => (
                            <Chip
                              key={idx}
                              variant="soft"
                              color="primary"
                              startDecorator={<Tag />}
                              size="sm"
                            >
                              {keyword}
                            </Chip>
                          ))}
                      </Box>
                      {map && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Chip
                            variant="soft"
                            color={getStatusColor(map.status)}
                            startDecorator={getStatusIcon(map.status)}
                            size="sm"
                          >
                            {map.status}
                          </Chip>
                          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                            {map.ideas_count || 0} blog ideas
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {generatingPages.has(page.id) ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CircularProgress size="sm" />
                          <Typography level="body-sm">Generating ideas...</Typography>
                        </Box>
                      ) : map ? (
                        <>
                          <Button
                            variant="outlined"
                            color="primary"
                            size="sm"
                            onClick={() => handleViewIdeas(page, map.id)}
                          >
                            View Ideas
                          </Button>
                          {map.status === 'draft' && (
                            <Button
                              variant="solid"
                              color="success"
                              size="sm"
                              onClick={() => approveTopicalMap(map.id)}
                            >
                              Approve
                            </Button>
                          )}
                        </>
                      ) : (
                        <Button
                          variant="solid"
                          color="primary"
                          size="sm"
                          startDecorator={<AutoAwesome />}
                          onClick={() => handleGenerateMap(page)}
                        >
                          Generate Ideas
                        </Button>
                      )}
                    </Stack>
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      {/* Blog Ideas Modal */}
      <Modal open={showBlogIdeas} onClose={() => setShowBlogIdeas(false)}>
        <ModalDialog sx={{ maxWidth: '90vw', width: 800, maxHeight: '90vh', overflow: 'auto' }}>
          <ModalClose />
          <Typography level="h3" sx={{ mb: 2 }}>
            Blog Ideas for {selectedTopPage?.url || 'Top Page'}
          </Typography>
          <Divider sx={{ my: 2 }} />
          {selectedMapId && (
            <BlogIdeaList
              topicalMapId={selectedMapId}
              onClose={() => setShowBlogIdeas(false)}
            />
          )}
        </ModalDialog>
      </Modal>

      {/* Import From Website Modal */}
      <ImportWebsitePagesModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImported={handleImported}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        color={snackbar.color}
        variant="soft"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar.message}
      </Snackbar>
    </Box>
  );
};